"use client";

import { memo, useCallback } from "react";
import { t } from "@/utils";
import { HmcInputField } from "@/components/hmc-on-server";
import { useController, useFormContext } from "react-hook-form";
import { HmcMessage } from "@adi-ctx/harmonic-core-components-react";

type FormInputProps = {
	readonly [key: string]: any;
	readonly className?: string;
	readonly hasError: boolean;
	readonly field: string;
	readonly label: string;
	readonly placeholder?: string;
	readonly icon?: any;
	readonly helperText?: React.ReactNode;
	readonly isRequired?: boolean;
	readonly inputPrefix?: string;
};

/**
 * Input form field
 * @constructor
 */
function FormInput(props: FormInputProps) {
	const { control } = useFormContext();
	const { field } = useController({
		control,
		name: props.field,
		rules: { required: Boolean(props.isRequired) },
	});

	const onChange = useCallback(
		({ detail: { value } }: { detail: { value: string } }) => {
			field.onChange(value);
		},
		[field]
	);

	return (
		<div className={`flex w-full flex-col ${props.className ?? ""}`}>
			<label
				className="hmc-subtitle-s mb-1 block"
				htmlFor="hmc-input-id"
				data-testid="cms-solutions-form-url-input-label"
			>
				{props.label}
			</label>

			<div className="flex grow">
				{props.inputPrefix && (
					<span
						className="c-white hmc-subtitle-s h-[40px] rounded-l bg-[#848B95] py-[10px] pl-4 pr-2"
						data-testid="cms-form-input-prefix"
					>
						{props.inputPrefix}
					</span>
				)}
				<HmcInputField
					isFullWidth
					isClearable
					status={props.hasError ? "error" : null}
					onHmcInput={onChange}
					{...props}
					className="-ml-[2px] grow rounded-l bg-white"
					helperText=""
					label=""
				>
					{props.icon && <props.icon size={24} slot="prefix" />}
				</HmcInputField>
			</div>

			{(props.hasError || props.helperText) && (
				<HmcMessage
					status={
						props.hasError ? "error" : props.helperText ? "info" : undefined
					}
					className="mt-1 block"
					data-testid="cms-form-input-hint"
				>
					{props.hasError
						? t("pages.cms.required-field", {
								field: props.label,
							})
						: props.helperText
							? props.helperText
							: ""}
				</HmcMessage>
			)}
		</div>
	);
}

export default memo(FormInput);
